import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { useAuth } from './AuthContext';
import adminApi from '../services/adminApi';
import { getApiErrorMessage } from '../utils/apiErrorMessage';
import i18n from '../i18n';

const AdminContext = createContext(null);

export const useAdmin = () => {
  const context = useContext(AdminContext);
  if (!context) {
    // AdminContext optional olabilir (AuthContext'te kullanılabilmesi için)
    return null;
  }
  return context;
};

export const AdminProvider = ({ children }) => {
  const { user, authToken, refreshUser } = useAuth();
  const [impersonatedUser, setImpersonatedUser] = useState(null);
  const [impersonateToken, setImpersonateToken] = useState(null);
  const [isImpersonating, setIsImpersonating] = useState(false);

  // Logout event'ini dinle ve impersonate state'ini temizle
  useEffect(() => {
    const handleLogout = () => {
      setImpersonatedUser(null);
      setImpersonateToken(null);
      setIsImpersonating(false);
    };

    window.addEventListener('notesplus-user-logged-out', handleLogout);
    return () => {
      window.removeEventListener('notesplus-user-logged-out', handleLogout);
    };
  }, []);

  // Kullanıcı bilgilerini yenile (login sonrası veya sayfa yüklendiğinde)
  useEffect(() => {
    if (user?.id && authToken && refreshUser) {
      // Kısa bir gecikme ile refresh yap (login işlemi tamamlansın)
      const timer = setTimeout(() => {
        refreshUser();
      }, 500);
      
      return () => clearTimeout(timer);
    }
  }, [user?.id, authToken, refreshUser]);

  // Admin kontrolü - membershipType === 'admin' olan kullanıcılar da admin sayılır
  // isAdmin boolean veya integer (1) olabilir
  // FIX: user veya authToken null olduğunda isAdmin false olmalı (logout sonrası admin paneli görünmemeli)
  const isAdmin = 
    user && 
    authToken && 
    (user.role === 'admin' || 
     user.isAdmin === true || 
     user.isAdmin === 1 ||
     user.membershipType === 'admin' || 
     user.membership_type === 'admin');

  // Impersonate başlat
  const startImpersonate = useCallback(
    async (userId) => {
      if (!isAdmin || !authToken) {
        return { success: false, error: 'Yetkiniz yok' };
      }

      try {
        const result = await adminApi.impersonateUser({ token: authToken, userId });
        if (result.success) {
          setImpersonatedUser(result.user);
          setImpersonateToken(result.impersonateToken);
          setIsImpersonating(true);
          return { success: true };
        }
        return { success: false, error: result.error || 'Impersonate başlatılamadı' };
      } catch (error) {
        console.error('Impersonate hatası:', error);
        return { success: false, error: getApiErrorMessage(error, { fallback: i18n.t('errors:GENERIC_ERROR') }) };
      }
    },
    [isAdmin, authToken]
  );

  // Impersonate sonlandır
  const stopImpersonate = useCallback(() => {
    setImpersonatedUser(null);
    setImpersonateToken(null);
    setIsImpersonating(false);
  }, []);

  // Aktif kullanıcı ID'sini döndür (impersonate durumunda impersonated user'ın ID'si)
  const getActiveUserId = useCallback(() => {
    if (isImpersonating && impersonatedUser) {
      return impersonatedUser.id;
    }
    return user?.id;
  }, [isImpersonating, impersonatedUser, user]);

  // Aktif kullanıcı bilgisini döndür
  const getActiveUser = useCallback(() => {
    if (isImpersonating && impersonatedUser) {
      return impersonatedUser;
    }
    return user;
  }, [isImpersonating, impersonatedUser, user]);

  // Aktif token'ı döndür (impersonate durumunda impersonate token)
  const getActiveToken = useCallback(() => {
    if (isImpersonating && impersonateToken) {
      return impersonateToken;
    }
    return authToken;
  }, [isImpersonating, impersonateToken, authToken]);

  const value = {
    isAdmin,
    isImpersonating,
    impersonatedUser,
    startImpersonate,
    stopImpersonate,
    getActiveUserId,
    getActiveUser,
    getActiveToken,
  };

  return <AdminContext.Provider value={value}>{children}</AdminContext.Provider>;
};

export default AdminContext;

